import { Fragment, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  paymentReconciliationApi,
  type ReconciliationActivityRow,
  type StudentProgramReconciliation,
} from "@/lib/api/payment-reconciliation";
import { badgeClass, formatCurrency } from "./reconciliation-shared";

type Props = {
  initialSearch?: string;
};

export function StudentActivityTab({ initialSearch = "" }: Props) {
  const [search, setSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StudentProgramReconciliation | null>(null);
  const [openProgram, setOpenProgram] = useState<string | null>(null);
  const searchRequestRef = useRef(0);

  useEffect(() => {
    if (initialSearch) {
      setSearch(initialSearch);
      void runSearch(initialSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when deep-link search changes
  }, [initialSearch]);

  const runSearch = async (q?: string) => {
    const term = (q ?? search).trim();
    if (!term) {
      setData(null);
      return;
    }
    const requestId = ++searchRequestRef.current;
    setLoading(true);
    try {
      const res = await paymentReconciliationApi.getStudent(term);
      if (requestId !== searchRequestRef.current) return;
      setData(res.data);
      setOpenProgram(null);
    } catch {
      if (requestId !== searchRequestRef.current) return;
      setData(null);
      toast.error("Student not found");
    } finally {
      if (requestId === searchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <div className="pr-card">
      <div className="pr-card-head">
        <div>
          <h2>Student Activity</h2>
          <p>
            Search by student name, email, or phone. After search, review program list first, then open payment
            history for any program.
          </p>
        </div>
      </div>
      <div className="pr-student-search-area">
        <div className="pr-field">
          <label>Search Student</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Enter name, email, or phone..."
            onKeyDown={(e) => e.key === "Enter" && void runSearch()}
          />
        </div>
        <button type="button" className="pr-primary-btn" onClick={() => void runSearch()}>
          Search
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !data ? (
        <div className="pr-empty-state">
          <strong>Search a student to begin</strong>
          Enter a name, email, or phone number to view the program list and compare school payment history with
          Square.
        </div>
      ) : (
        <div style={{ padding: "22px 24px" }}>
          <div className="pr-student-profile-strip">
            <div>
              <h3 style={{ margin: "0 0 5px", fontSize: 20, fontWeight: 800 }}>{data.student.name}</h3>
              <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
                {data.student.email} · {data.student.phone}
                <br />
                {data.programs.length} program{data.programs.length === 1 ? "" : "s"} found for this student.
              </p>
            </div>
            <span className="pr-badge purple">
              {data.programs.length} Program{data.programs.length === 1 ? "" : "s"}
            </span>
          </div>

          <div style={{ border: "1px solid #e6e8ef", borderRadius: 24, overflow: "hidden" }}>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid #edf0f4", fontWeight: 800 }}>
              Program List
            </div>
            <table className="pr-table" style={{ border: "none", borderRadius: 0 }}>
              <thead>
                <tr>
                  <th>Program</th>
                  <th>Plan</th>
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Outstanding</th>
                  <th>Installments</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.programs.map((p) => (
                  <Fragment key={p.program}>
                    <tr>
                      <td className="pr-student-cell">
                        <strong>
                          {p.program} ({p.programCode})
                        </strong>
                        <span>{p.enrollment}</span>
                      </td>
                      <td>
                        <strong>{p.planType}</strong>
                        <div style={{ color: "#64748b", fontSize: 12 }}>{p.plan}</div>
                      </td>
                      <td>
                        <strong>{formatCurrency(p.total)}</strong>
                      </td>
                      <td>
                        <strong>{formatCurrency(p.paid)}</strong>
                      </td>
                      <td>
                        <strong>{formatCurrency(p.outstanding)}</strong>
                      </td>
                      <td>
                        <strong>{p.totalInstallments}</strong>
                        <div style={{ color: "#64748b", fontSize: 12 }}>{p.remaining} remaining</div>
                      </td>
                      <td>
                        <span className={`pr-badge ${p.status === "Needs Review" ? "yellow" : "blue"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="pr-pill-btn"
                          onClick={() => setOpenProgram(openProgram === p.program ? null : p.program)}
                        >
                          View Activity
                        </button>
                      </td>
                    </tr>
                    {openProgram === p.program && (
                      <tr>
                        <td colSpan={8} style={{ background: "var(--muted)", padding: 0 }}>
                          <ProgramDualGrid program={p} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgramDualGrid({
  program,
}: {
  program: StudentProgramReconciliation["programs"][number];
}) {
  const records = [...program.records].sort((a, b) => (a.installmentNo ?? 0) - (b.installmentNo ?? 0));

  return (
    <div className="pr-program-dual-grid">
      <div className="pr-program-column school">
        <h4>School Portal Activity</h4>
        <div className="pr-program-stats">
          <div className="pr-program-stat">
            <span>Next Payment</span>
            <strong>{program.nextPayment}</strong>
          </div>
          <div className="pr-program-stat">
            <span>Next Amount</span>
            <strong>{formatCurrency(program.nextAmount)}</strong>
          </div>
          <div className="pr-program-stat">
            <span>Remaining Installments</span>
            <strong>{program.remaining}</strong>
          </div>
        </div>
        <div style={{ padding: "14px" }}>
          <InstallmentTable records={records} mode="school" />
        </div>
      </div>
      <div className="pr-program-column square">
        <h4>Square Activity</h4>
        <div className="pr-program-stats">
          <div className="pr-program-stat">
            <span>Payment Method</span>
            <strong>{program.paymentMethod}</strong>
          </div>
          <div className="pr-program-stat">
            <span>Card</span>
            <strong>{program.cardMasked}</strong>
          </div>
          <div className="pr-program-stat">
            <span>Card Type</span>
            <strong>{program.cardType}</strong>
          </div>
        </div>
        <div style={{ padding: "14px" }}>
          <InstallmentTable records={records} mode="square" />
        </div>
      </div>
    </div>
  );
}

function InstallmentTable({
  records,
  mode,
}: {
  records: ReconciliationActivityRow[];
  mode: "school" | "square";
}) {
  return (
    <table className="pr-table" style={{ fontSize: 12 }}>
      <thead>
        <tr>
          <th>#</th>
          <th>{mode === "school" ? "Payment date" : "Square date"}</th>
          {mode === "square" && <th>Transaction</th>}
          {mode === "school" && <th>Type</th>}
          <th>Amount</th>
          <th>Status{mode === "square" ? " / Result" : ""}</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r) => (
          <tr key={`${mode}-${r.id}`}>
            <td>
              {r.installmentNo ?? "—"} / {r.totalInstallments}
            </td>
            <td>
              <strong>{mode === "school" ? r.date : r.squareDate}</strong>
              {mode === "school" && r.school === "Overdue" && (
                <div style={{ color: "#dc2626", fontSize: 11 }}>Past due</div>
              )}
            </td>
            {mode === "square" && <td>{r.txn}</td>}
            {mode === "school" && <td>{r.planType}</td>}
            <td>
              <strong>{formatCurrency(r.amount)}</strong>
              {mode === "school" &&
                r.school === "Overdue" &&
                (r.lateFeeAmount ?? 0) > 0 && (
                  <div
                    style={{
                      color: r.lateFeeApplied ? "#dc2626" : "#b45309",
                      fontSize: 11,
                      marginTop: 4,
                    }}
                  >
                    {r.lateFeeApplied
                      ? `+ ${formatCurrency(r.lateFeeAmount!)} late fee ${
                          r.lateFeeStatus === "PENDING" ? "pending" : "applied"
                        }`
                      : `+ ${formatCurrency(r.lateFeeAmount!)} late fee due`}
                    <div style={{ color: "#64748b" }}>
                      Total{" "}
                      {formatCurrency(
                        r.amountWithLateFee ?? r.amount + (r.lateFeeAmount ?? 0)
                      )}
                    </div>
                  </div>
                )}
            </td>
            <td>
              <span className={`pr-badge ${badgeClass(mode === "school" ? r.school : r.square)}`}>
                {mode === "school" ? r.school : r.square}
              </span>
              {mode === "school" && r.school === "Overdue" && r.lateFeeApplied && (
                <div style={{ color: "#b45309", fontSize: 11, marginTop: 6 }}>
                  Late fee {r.lateFeeStatus === "PENDING" ? "pending" : "applied"}
                </div>
              )}
              {mode === "square" && (
                <div style={{ color: "#64748b", fontSize: 11, marginTop: 6 }}>{r.result}</div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
